import { DeleteOutlined, FileOutlined, UploadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Image, Input, List, Popconfirm, Space, Typography, Upload } from "antd";
import { useState } from "react";
import { API_BASE, api, errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";

interface Attachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  is_image: boolean;
  comment: string | null;
  created_at: string;
  created_by: string | null;
  url: string;
}

async function fetchBlobUrl(id: string): Promise<string> {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${API_BASE}/attachments/file/${id}/download`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) throw new Error("Не удалось загрузить файл");
  return URL.createObjectURL(await response.blob());
}

/** Files and images of a record (ФТ-1.10): upload, preview of images, download, delete. */
export function AttachmentsTab({ entity, id }: { entity: string; id: string }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const token = useAuthStore((s) => s.accessToken);
  const [comment, setComment] = useState("");
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const list = useQuery({ queryKey: ["attachments", entity, id], queryFn: () => api.get<Attachment[]>(`/attachments/${entity}/${id}`) });
  const remove = useMutation({
    mutationFn: (attachmentId: string) => api.delete(`/attachments/file/${attachmentId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["attachments", entity, id] }),
    onError: (e) => message.error(errorMessage(e)),
  });
  const open = async (a: Attachment) => {
    try {
      const url = previews[a.id] ?? (await fetchBlobUrl(a.id));
      setPreviews((p) => ({ ...p, [a.id]: url }));
      if (a.is_image) return;
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  return (
    <div>
      {can("core.attachments.write") && (
        <Space style={{ marginBottom: 12 }} wrap>
          <Input placeholder="Комментарий к файлу" value={comment} onChange={(e) => setComment(e.target.value)} style={{ width: 260 }} />
          <Upload
            name="file"
            showUploadList={false}
            action={`${API_BASE}/attachments/${entity}/${id}${comment ? `?comment=${encodeURIComponent(comment)}` : ""}`}
            headers={token ? { Authorization: `Bearer ${token}` } : {}}
            onChange={(info) => {
              if (info.file.status === "done") {
                message.success(`Файл ${info.file.name} загружен`);
                setComment("");
                queryClient.invalidateQueries({ queryKey: ["attachments", entity, id] });
              } else if (info.file.status === "error") {
                message.error((info.file.response as { detail?: string } | undefined)?.detail ?? "Ошибка загрузки");
              }
            }}
          >
            <Button icon={<UploadOutlined />}>Загрузить файл</Button>
          </Upload>
        </Space>
      )}
      <List
        loading={list.isLoading}
        dataSource={list.data ?? []}
        locale={{ emptyText: "Файлов нет" }}
        renderItem={(a) => (
          <List.Item
            actions={[
              <Button key="open" type="link" onClick={() => open(a)}>
                {a.is_image ? "Показать" : "Скачать"}
              </Button>,
              ...(can("core.attachments.write")
                ? [
                    <Popconfirm key="del" title="Удалить файл?" okText="Удалить" cancelText="Отмена" onConfirm={() => remove.mutate(a.id)}>
                      <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]
                : []),
            ]}
          >
            <List.Item.Meta
              avatar={previews[a.id] && a.is_image ? <Image src={previews[a.id]} width={64} /> : <FileOutlined style={{ fontSize: 24 }} />}
              title={a.filename}
              description={
                <Typography.Text type="secondary">
                  {(a.size / 1024).toFixed(1)} КБ · {new Date(a.created_at).toLocaleString("ru-RU")} · {a.created_by ?? ""} {a.comment ? `· ${a.comment}` : ""}
                </Typography.Text>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );
}
